import { useContext } from 'react';
import { Navigate } from 'react-router-dom';
import AuthContext from '../../context/authContext';
import { Box } from '@mui/material';
import EditableList from '../EditableList/EditableList';
import { viewRootTextPrimarySx } from '../../util/chartTheme';

function AvailabilityManagement() {
  const authCtx = useContext(AuthContext);

  // Access rights
  if (!authCtx.checkRight('availability:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const availabilitySchema = {
    name: {
      label: 'Name',
      api: { itemField: 'name' },
      placeholder: 'Name eingeben',
      type: 'text',
      required: true,
      minWidth: '200px',
    },
    priority: {
      label: 'Priorität',
      api: { itemField: 'priority' },
      placeholder: 'Priorität (Zahl)',
      type: 'number',
      required: true,
      minWidth: '120px',
    },
    validFrom: {
      label: 'Gültig von',
      api: { itemField: 'validFrom' },
      placeholder: 'Datum auswählen',
      type: 'date',
      required: true,
      minWidth: '160px',
    },
    validTo: {
      label: 'Gültig bis',
      api: { itemField: 'validTo' },
      placeholder: 'Datum auswählen',
      type: 'date',
      required: true,
      minWidth: '160px',
    },
    specialTypes: {
      label: 'Spezialtypen',
      type: 'multi-select',
      placeholder: 'Spezialtypen wählen',
      required: false,
      minWidth: '200px',
      options: [
        { value: 'holiday', label: 'Feiertag' },
        { value: 'vacation', label: 'Ferien' },
        { value: 'summerVacation', label: 'Sommerferien' },
        { value: 'bridgeDay', label: 'Brückentag' },
      ],
    },
    allowOutsideOpeningHours: {
      label: 'Außerhalb Öffnungszeiten zulassen',
      api: { itemField: 'allowOutsideOpeningHours' },
      type: 'boolean',
      required: false,
      minWidth: '260px',
    },
    hours: {
      label: 'Zeitslots',
      type: 'nested',
      addButtonText: 'Neue Zeit hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'hours',
        nestedEndpoint: 'hour',
      },
      schema: {
        dayCode: {
          label: 'Wochentage (0=So, 1=Mo, ...)',
          type: 'array',
          placeholder: 'z. B. 1,2,3',
          transformValue: value =>
            value
              .toString()
              .split(',')
              .map(v => parseInt(String(v).trim(), 10))
              .filter(v => !Number.isNaN(v)),
          listDisplayValue: value => {
            if (!value || !Array.isArray(value) || value.length === 0) return '—';
            return value.join(', ');
          },
          required: true,
        },
        start: {
          label: 'Start (HH:MM)',
          type: 'time',
          required: true,
        },
        end: {
          label: 'Ende (HH:MM)',
          type: 'time',
          required: true,
        },
      },
    },
    assignedEntertainments: {
      label: 'Zugeordnete Entertainments',
      type: 'multi-select',
      api: {
        fieldName: 'entertainment',
        itemField: 'assignedEntertainments',
        idName: 'entertainmentId',
        resource: 'entertainment',
        labelField: 'longName',
        valueField: '_id',
        skipSpecial: true,
        objectify: { idProp: 'entertainmentId', labelProp: 'name' },
      },
      placeholder: 'Entertainments auswählen',
      required: false,
      listDisplayValue: values => {
        if (!Array.isArray(values) || values.length === 0) return '—';
        return values
          .map(v => v?.name || '')
          .filter(Boolean)
          .join(', ');
      },
    },
    assignedProducts: {
      label: 'Zugeordnete Produkte',
      type: 'nested',
      addButtonText: 'Produktzuordnung hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'assignedProducts',
        nestedEndpoint: 'assignedProduct',
      },
      schema: {
        productId: {
          label: 'Produkt',
          type: 'select',
          required: true,
          readOnly: true,
          api: {
            resource: 'product',
            labelField: 'internalName',
            valueField: '_id',
            skipSpecial: true,
          },
        },
        name: {
          label: 'Produktname',
          type: 'text',
          readOnly: true,
          excludeFromAdd: true,
        },
        pricelistId: {
          label: 'Preisliste',
          type: 'select',
          required: true,
          api: {
            resourceById: 'product',
            dependentIdField: 'productId',
            path: 'pricing',
            labelField: 'list',
            valueField: '_id',
          },
        },
        pricelistName: {
          label: 'Listenname',
          type: 'text',
          excludeFromAdd: true,
          readOnly: true,
        },
        active: {
          label: 'Aktiv',
          type: 'boolean',
          required: false,
        },
      },
    },
    entertainmentProductRelations: {
      label: 'Entertainment-Produkt-Relationen',
      type: 'nested',
      addButtonText: 'Relation hinzufügen',
      excludeFromAdd: true,
      api: {
        itemField: 'entertainmentProductRelations',
        nestedEndpoint: 'entertainmentProductRelation',
      },
      schema: {
        entertainmentIds: {
          label: 'Entertainments',
          type: 'multi-select',
          required: true,
          api: {
            resource: 'entertainment',
            labelField: 'longName',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Entertainments auswählen',
          listDisplayValue: (values, item) => {
            const count = Array.isArray(values) ? values.length : 0;
            const productName = item?.productName || '—';
            return count > 0 ? `${productName} (${count} Entertainment${count !== 1 ? 's' : ''})` : productName;
          },
        },
        productId: {
          label: 'Produkt',
          type: 'select',
          required: true,
          api: {
            resource: 'product',
            labelField: 'internalName',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Produkt auswählen',
        },
        pricelistId: {
          label: 'Preisliste',
          type: 'select',
          required: true,
          api: {
            resourceById: 'product',
            dependentIdField: 'productId',
            path: 'pricing',
            labelField: 'list',
            valueField: '_id',
          },
          placeholder: 'Preisliste auswählen',
        },
        resourceId: {
          label: 'Ressource (optional)',
          type: 'select',
          required: false,
          api: {
            resource: 'resource',
            labelField: 'name',
            valueField: '_id',
            skipSpecial: true,
          },
          placeholder: 'Ressource auswählen',
        },
        active: {
          label: 'Aktiv',
          type: 'boolean',
          required: false,
        },
        // Optional time-slot override for this relation. When at least one slot
        // is set here, these slots replace the availability-level slots above for
        // this entertainment+product combination. Empty = use availability slots.
        hours: {
          label: 'Zeitslot-Override (optional)',
          type: 'nested',
          addButtonText: 'Override-Zeit hinzufügen',
          excludeFromAdd: true,
          api: {
            itemField: 'hours',
            nestedEndpoint: 'hour',
          },
          schema: {
            dayCode: {
              label: 'Wochentage (0=So, 1=Mo, ...)',
              type: 'array',
              placeholder: 'z. B. 1,2,3',
              transformValue: value =>
                value
                  .toString()
                  .split(',')
                  .map(v => parseInt(String(v).trim(), 10))
                  .filter(v => !Number.isNaN(v)),
              listDisplayValue: value => {
                if (!value || !Array.isArray(value) || value.length === 0) return '—';
                return value.join(', ');
              },
              required: true,
            },
            start: {
              label: 'Start (HH:MM)',
              type: 'time',
              required: true,
            },
            end: {
              label: 'Ende (HH:MM)',
              type: 'time',
              required: true,
            },
            durationsMinutes: {
              label: 'Durations (Min., leer = alle)',
              type: 'array',
              placeholder: 'z. B. 60,90',
              transformValue: value =>
                value
                  .toString()
                  .split(',')
                  .map(v => parseInt(String(v).trim(), 10))
                  .filter(v => !Number.isNaN(v) && v > 0),
              listDisplayValue: value => {
                if (!value || !Array.isArray(value) || value.length === 0) return 'Alle';
                return value.map(v => `${v} Min`).join(', ');
              },
              required: false,
            },
          },
        },
      },
    },
  };

  return (
    <Box sx={viewRootTextPrimarySx}>
    <EditableList
      schema={availabilitySchema}
      title='Neue Verfügbarkeit anlegen'
      resourceType='availability'
      headerSearchPlaceholder='Verfügbarkeiten durchsuchen'
      parentIsEditable={authCtx.checkRight('availability:update')}
      options={{
        titleKey: 'name',
      }}
    />
    </Box>
  );
}

export default AvailabilityManagement;
